import { useState, useEffect, useMemo, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, ModalFooter, Input, Select, Button } from '@tkpod/ui';
import { api } from '@/lib/api';
import { ImageUploadInput } from '@/components/ImageUploadInput';
import type { CategoryNode } from './CategoryTree';

interface CategoryFormModalProps {
  open: boolean;
  onClose: () => void;
  category?: CategoryNode | null;
  allCategories: CategoryNode[];
}

interface FormState {
  name: string;
  slug: string;
  description: string;
  imageUrl: string;
  parentId: string;
  sortOrder: string;
  isActive: boolean;
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function flattenCategories(nodes: CategoryNode[]): CategoryNode[] {
  const result: CategoryNode[] = [];
  for (const node of nodes) {
    result.push(node);
    if (node.children.length > 0) {
      result.push(...flattenCategories(node.children));
    }
  }
  return result;
}

function getDescendantIds(node: CategoryNode): Set<string> {
  const ids = new Set<string>();
  function walk(n: CategoryNode) {
    ids.add(n.id);
    for (const child of n.children) walk(child);
  }
  walk(node);
  return ids;
}

export function CategoryFormModal({
  open,
  onClose,
  category,
  allCategories,
}: CategoryFormModalProps) {
  const isEdit = !!category;
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>({
    name: '',
    slug: '',
    description: '',
    imageUrl: '',
    parentId: '',
    sortOrder: '0',
    isActive: true,
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (category) {
        setForm({
          name: category.name,
          slug: category.slug,
          description: category.description ?? '',
          imageUrl: category.imageUrl ?? '',
          parentId: category.parentId ?? '',
          sortOrder: String(category.sortOrder),
          isActive: category.isActive,
        });
        setSlugTouched(true);
      } else {
        setForm({ name: '', slug: '', description: '', imageUrl: '', parentId: '', sortOrder: '0', isActive: true });
        setSlugTouched(false);
      }
      setError(null);
    }
  }, [open, category]);

  const parentOptions = useMemo(() => {
    const flat = flattenCategories(allCategories);
    const excludeIds = category ? getDescendantIds(category) : new Set<string>();

    const options = flat
      .filter((c) => !excludeIds.has(c.id))
      .map((c) => ({ value: c.id, label: c.name }));

    return [{ value: '', label: 'None (root category)' }, ...options];
  }, [allCategories, category]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value;
      setForm((prev) => ({
        ...prev,
        name,
        slug: slugTouched ? prev.slug : generateSlug(name),
      }));
    },
    [slugTouched],
  );

  const handleSlugChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSlugTouched(true);
    setForm((prev) => ({ ...prev, slug: e.target.value }));
  }, []);

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post('/api/v1/admin/catalog/categories', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-categories-tree'] });
      queryClient.invalidateQueries({ queryKey: ['catalog-categories'] });
      onClose();
    },
    onError: (err: Error) => setError(err.message || 'Failed to create category.'),
  });

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/api/v1/admin/catalog/categories/${category!.id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-categories-tree'] });
      queryClient.invalidateQueries({ queryKey: ['catalog-categories'] });
      onClose();
    },
    onError: (err: Error) => setError(err.message || 'Failed to update category.'),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!form.slug.trim()) {
      setError('Slug is required.');
      return;
    }

    const body: Record<string, unknown> = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      sortOrder: parseInt(form.sortOrder, 10) || 0,
    };

    if (form.description.trim()) body.description = form.description.trim();
    if (form.imageUrl) body.imageUrl = form.imageUrl;
    if (isEdit && !form.imageUrl && category?.imageUrl) body.imageUrl = null;
    if (form.parentId) body.parentId = form.parentId;
    if (isEdit) body.isActive = form.isActive;

    if (isEdit) {
      updateMutation.mutate(body);
    } else {
      createMutation.mutate(body);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Category' : 'Create Category'}
      description={
        isEdit
          ? 'Update category details below.'
          : 'Add a new product category to organize your catalog.'
      }
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          placeholder="e.g. T-Shirts"
          value={form.name}
          onChange={handleNameChange}
          required
        />

        <Input
          label="Slug"
          placeholder="e.g. t-shirts"
          value={form.slug}
          onChange={handleSlugChange}
          hint="URL-friendly identifier. Auto-generated from name."
          required
        />

        <div className="w-full">
          <label
            htmlFor="category-description"
            className="block text-sm font-medium text-text-primary mb-1.5"
          >
            Description
          </label>
          <textarea
            id="category-description"
            rows={3}
            placeholder="Optional description for this category"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-brand-500/30 focus:border-brand-500"
          />
        </div>

        <ImageUploadInput
          label="Category Image"
          value={form.imageUrl}
          directory="categories"
          onChange={(url) => setForm((prev) => ({ ...prev, imageUrl: url }))}
          previewSize="lg"
        />

        <Select
          label="Parent Category"
          value={form.parentId}
          onChange={(e) => setForm((prev) => ({ ...prev, parentId: e.target.value }))}
          options={parentOptions}
        />

        <Input
          label="Sort Order"
          type="number"
          min={0}
          value={form.sortOrder}
          onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
          hint="Lower numbers appear first."
        />

        {isEdit && (
          <div className="flex items-center gap-2">
            <input
              id="category-active"
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
            />
            <label htmlFor="category-active" className="text-sm text-text-primary">
              Active
            </label>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2.5 rounded-lg border border-danger-200 bg-danger-50 p-3">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 shrink-0 text-danger-500 mt-px">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-danger-700">{error}</p>
          </div>
        )}

        <ModalFooter>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" size="sm" loading={isPending}>
            {isEdit ? 'Save Changes' : 'Create Category'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
